import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { ArrowLeft, AlertTriangle, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { HitlActionBar } from "@/components/hitl/HitlActionBar";
import { CountdownTimer } from "@/components/hitl/CountdownTimer";
import { EvidencePanel } from "@/components/incident/EvidencePanel";
import { SatelliteMapInset } from "@/components/SatelliteMapInset";
import { StatusBadge } from "@/components/StatusBadge";
import type { AuditEntry, IncidentDto, RelatedSegment } from "@/lib/types";

interface IncidentDetailResponse {
  incident: IncidentDto;
  audit: AuditEntry[];
  // §5.9 R-02 — populated by the correlation engine in the classify-worker.
  // Empty array (not undefined) when correlation hasn't run or returned 0
  // matches. UI hides the related-segments card in that case.
  relatedSegments: RelatedSegment[];
  // `null` until correlation runs at least once for this incident, then
  // "rule-v1" (or "ml-v1" once Phase 2.2 lands).
  correlationRuleVersion: string | null;
  // §5.8 S-04 — segment centroid GPS for the satellite map inset.
  // `null` if the segment has no centroid recorded.
  segmentLat: number | null;
  segmentLng: number | null;
}

interface SitesResponse {
  sites: Array<{ id: string; siteId: string }>;
}

interface OpsOverviewResponse {
  segments: Array<{
    segmentId: string;
    centroidLat: number | null;
    centroidLng: number | null;
  }>;
}

export function IncidentDetailPage() {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const incidentId = params.id;

  const { data, isLoading, error, refetch } = useQuery<IncidentDetailResponse>({
    queryKey: ["incident", incidentId],
    queryFn: () => api<IncidentDetailResponse>(`/incidents/${incidentId}`),
    refetchInterval: 5000,
    enabled: Boolean(incidentId),
  });

  // Day 10 — fetch corridor anchor coordinates so the satellite inset can
  // overlay the pipeline polyline. Two-step: list sites, then ops-overview
  // for the matching site. The site list is small (1 row in pilot) and
  // both queries cache for a minute, so the cost is negligible.
  const sitesQuery = useQuery<SitesResponse>({
    queryKey: ["sites"],
    queryFn: () => api<SitesResponse>("/assets/sites"),
    staleTime: 60_000,
    enabled: Boolean(data?.incident.siteId),
  });
  const matchingSite = sitesQuery.data?.sites.find(
    (s) => s.siteId === data?.incident.siteId,
  );
  const overviewQuery = useQuery<OpsOverviewResponse>({
    queryKey: ["ops-overview", matchingSite?.id],
    queryFn: () =>
      api<OpsOverviewResponse>(
        `/assets/sites/${matchingSite!.id}/ops-overview`,
      ),
    staleTime: 60_000,
    enabled: Boolean(matchingSite?.id),
  });

  // Memoised so the array reference is stable across re-renders that
  // don't change the underlying segments — keeps SatelliteMapInset's
  // useEffect deps from firing spuriously.
  const pipelineAnchors = useMemo<Array<[number, number]>>(
    () =>
      (overviewQuery.data?.segments ?? [])
        .filter(
          (s): s is typeof s & { centroidLat: number; centroidLng: number } =>
            s.centroidLat != null && s.centroidLng != null,
        )
        .map((s) => [s.centroidLng, s.centroidLat]),
    [overviewQuery.data?.segments],
  );

  if (isLoading) {
    return (
      <div className="text-sm text-slate-400" data-testid="incident-loading">
        {t("dashboard.loading")}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="text-sm text-rose-400" data-testid="incident-error">
        {t("incident.noAccess")}
      </div>
    );
  }

  const {
    incident,
    audit,
    relatedSegments,
    correlationRuleVersion,
    segmentLat,
    segmentLng,
  } = data;

  return (
    <div className="space-y-4" data-testid="incident-detail-page">
      <Link
        href="/app"
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
        data-testid="incident-back"
      >
        <ArrowLeft className="size-3" /> {t("incident.back")}
      </Link>

      {/* §5.9 R-03 — recommend-supervisor-escalation banner. Visible when the
          correlation engine flagged ≥ CORRELATION_AUTO_ESCALATE_THRESHOLD
          related anomalies in the same site/window. The banner is purely
          advisory; H-01 invariant means the operator still chooses whether
          to escalate. */}
      {incident.correlationPattern && (
        <div
          role="alert"
          data-testid="correlation-recommend-banner"
          className="flex items-start gap-2 rounded-lg border border-amber-700/70 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"
        >
          <AlertTriangle className="mt-0.5 size-4 flex-shrink-0 text-amber-300" />
          <div className="space-y-0.5">
            <div className="font-semibold">
              {t("incident.correlation.recommendTitle", {
                defaultValue: "Recommend supervisor escalation",
              })}
            </div>
            <div className="text-xs text-amber-200/80">
              {t("incident.correlation.recommendBody", {
                defaultValue:
                  "The correlation engine found multiple related anomalies on nearby segments within the same window. Review the related-segments card below before deciding.",
              })}
            </div>
          </div>
        </div>
      )}

      <header className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <StatusBadge status={incident.status} />
              <span className="text-base font-semibold">
                {incident.eventType}
              </span>
              {incident.status === "DRAFT" && (
                <CountdownTimer createdAt={incident.createdAt} />
              )}
            </div>
            <div className="text-xs text-slate-400 font-mono">
              {incident.siteId} / {incident.segmentId} · {incident.id.slice(0, 8)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">{t("incident.confidence")}</div>
            <div
              className={`text-xl font-mono ${
                incident.confidence >= 0.8
                  ? "text-emerald-400"
                  : incident.confidence >= 0.5
                  ? "text-amber-300"
                  : "text-rose-400"
              }`}
              data-testid="confidence-pct"
            >
              {Math.round(incident.confidence * 100)}%
            </div>
          </div>
        </div>
      </header>

      <HitlActionBar
        incidentId={incident.id}
        status={incident.status}
        onAfterAction={() => void refetch()}
      />

      <EvidencePanel
        incident={{
          id: incident.id,
          event_type: incident.eventType,
          confidence: incident.confidence,
          reasoning: incident.reasoning,
          recommendedAction: incident.recommendedAction,
          modelVersion: incident.modelVersion,
          signalWindowRef: incident.signalWindowRef,
          eventTimestamp: incident.createdAt,
          siteId: incident.siteId,
          segmentId: incident.segmentId,
          contextSnapshot: incident.contextSnapshot,
        }}
      />

      {/* §5.9 R-02 — "Related segments to watch" card. Renders only when the
          correlation engine returned ≥1 result. The data appears even when
          the auto-escalate banner doesn't, so a single related segment is
          surfaced to the operator without recommending escalation. */}
      {relatedSegments.length > 0 && (
        <section
          className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
          data-testid="related-segments-card"
        >
          <header className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wide mb-3">
            <Activity className="size-3.5 text-teal-400" />
            <span>
              {t("incident.correlation.cardTitle", {
                defaultValue: "Related segments to watch",
              })}
            </span>
            <span className="ml-auto text-[10px] text-slate-500 font-mono normal-case">
              {correlationRuleVersion ?? "rule-v1"}
            </span>
          </header>
          <ul className="space-y-2" data-testid="related-segments-list">
            {relatedSegments.map((rs) => (
              <li
                key={rs.segmentId}
                className="rounded border border-slate-800/60 bg-slate-900/40 px-3 py-2"
                data-testid={`related-segment-${rs.segmentId}`}
              >
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-mono text-slate-200">{rs.segmentId}</span>
                  <span className="text-slate-500">
                    score{" "}
                    <span className="text-slate-200 font-mono">
                      {(rs.score * 100).toFixed(0)}%
                    </span>
                    {" · "}rms{" "}
                    <span className="text-slate-200 font-mono">
                      {rs.rmsRatio.toFixed(2)}×
                    </span>
                    {" · "}
                    <span className="text-slate-200 font-mono">
                      {Math.round(rs.timeOffsetSeconds / 60)} min ago
                    </span>
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-400">{rs.summary}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* §5.8 S-04 — Mapbox satellite inset. Renders fallback messages
          internally when the token is missing or the segment has no GPS
          recorded. The map itself is lazy-initialised on user click. */}
      <SatelliteMapInset
        incidentId={incident.id}
        segmentId={incident.segmentId}
        lat={segmentLat}
        lng={segmentLng}
        pipelineAnchors={pipelineAnchors}
        // cctvMarkers omitted — defaults to [] inside the component, which
        // is a single stable reference. Passing an inline `[]` here would
        // create a fresh reference each render and re-init the map.
      />

      <section
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-3"
        data-testid="audit-trail"
      >
        <header className="text-xs text-slate-400 uppercase tracking-wide mb-2">
          {t("incident.audit")}
        </header>
        <ul className="space-y-1.5">
          {audit.map((entry) => (
            <li key={entry.id} className="text-xs grid grid-cols-[140px_120px_1fr] gap-2">
              <span className="text-slate-500 font-mono">
                {new Date(entry.createdAt).toLocaleTimeString()}
              </span>
              <span className="text-slate-200">{entry.actionType}</span>
              <span className="text-slate-400">
                {entry.fromStatus && entry.toStatus
                  ? `${entry.fromStatus} → ${entry.toStatus}`
                  : entry.notes ?? "—"}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
